// ─── Super-admin Organizations panel: pure helpers ───
// No imports on purpose: tests/whitelabel.test.mjs imports this file straight into Node.

// Org-wide feature flags, grouped like the coach portal (ironlog-coach Flags.jsx).
// Keys mirror FLAG_KEYS in src/utils/org.js / FEATURE_KEYS in the API (22 keys).
export const FLAG_GROUPS = [
  { title: 'Tabs & social', keys: [
    ['social', 'Social tab', 'Hides the whole Social tab (feed, friends, groups, league, duels).'],
    ['feed', 'Activity feed', 'Hides the friends activity feed.'],
    ['friends', 'Friends', 'Hides friend codes, requests and the friends list.'],
    ['groups', 'Groups', 'Hides group creation and group chats.'],
    ['messages_social', 'Friend messaging', 'Hides direct messages between friends (coach messaging is separate).'],
    ['league', 'League', 'Hides the weekly league and rankings.'],
    ['duels', 'Duels', 'Hides 1-on-1 duels.'],
  ] },
  { title: 'Training', keys: [
    ['programs', 'Programs', 'Hides built-in training programs and templates.'],
    ['ai_coach', 'AI coach', 'Hides AI-generated workout suggestions and insights.'],
    ['ai_chat', 'AI chat', 'Hides the AI chat assistant.'],
    ['form_check', 'Form check', 'Hides the AI form-check upload.'],
    ['youtube_videos', 'Exercise videos', 'Hides YouTube demo links on exercises.'],
  ] },
  { title: 'Nutrition & body', keys: [
    ['nutrition', 'Nutrition tab', 'Hides macro tracking entirely.'],
    ['meal_plan', 'Meal plans', 'Hides meal ideas and plans.'],
    ['fast_food', 'Fast-food lookup', 'Hides the restaurant / fast-food database.'],
    ['supplements', 'Supplements', 'Hides the supplement tracker.'],
    ['photos', 'Progress photos', 'Hides progress photo capture and gallery.'],
  ] },
  { title: 'Sharing & other', keys: [
    ['share_cards', 'Share cards', 'Hides the shareable workout / PR image cards.'],
    ['messages', 'Coach messaging', 'Hides the coach inbox in the member app.'],
    ['install_banner', 'Install banner', 'Hides the "add to home screen" prompt.'],
    ['ecosystem_links', 'IronLog ecosystem links', 'Links to other IronLog apps. Off by default for organisations.'],
    ['donate_card', 'Donate card', 'The IronLog donation card. Off by default for organisations.'],
  ] },
];
export const FLAG_GROUP_KEYS = FLAG_GROUPS.flatMap(g => g.keys.map(k => k[0]));
// Keys that default OFF once a user is in an org (ORG_DEFAULT_OFF in the API).
export const ORG_DEFAULT_OFF = ['ecosystem_links', 'donate_card'];

export const flagDefault = (key) => !ORG_DEFAULT_OFF.includes(key);
// 'default' | 'on' | 'off' for the tri-state control, from the org's explicit map.
export const flagState = (orgFlags, key) => (typeof orgFlags?.[key] === 'boolean' ? (orgFlags[key] ? 'on' : 'off') : 'default');
// Effective org-level value (client overrides are per member and not shown here).
export const flagEffective = (orgFlags, key) => (typeof orgFlags?.[key] === 'boolean' ? orgFlags[key] : flagDefault(key));
// Body for set_org_flags from a tri-state choice (null clears the explicit row).
export const flagPatch = (key, state) => ({ [key]: state === 'default' ? null : state === 'on' });
// Apply a set_org_flags patch to the explicit map (what the API returns after the write).
export function mergeFlagPatch(orgFlags, patch) {
  const out = { ...(orgFlags || {}) };
  for (const [k, v] of Object.entries(patch || {})) { if (v === null || v === undefined) delete out[k]; else out[k] = !!v; }
  return out;
}
// Reset-all: null for every explicit key.
export const resetFlagsPatch = (orgFlags) => Object.fromEntries(Object.keys(orgFlags || {}).filter(k => typeof orgFlags[k] === 'boolean').map(k => [k, null]));
export const explicitFlagCount = (orgFlags) => Object.keys(orgFlags || {}).filter(k => typeof orgFlags[k] === 'boolean').length;

// ─── CSV ───
export const csvCell = (v) => {
  const s = v === null || v === undefined ? '' : (typeof v === 'object' ? JSON.stringify(v) : String(v));
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
export const AUDIT_CSV_COLUMNS = ['id', 'created_at', 'action', 'actor_email', 'target', 'detail'];
// Same shape as the API's audit_export CSV, so the client-side fallback is byte-compatible.
export const auditCsv = (entries) => [AUDIT_CSV_COLUMNS.join(','), ...(entries || []).map(r => AUDIT_CSV_COLUMNS.map(c => csvCell(r[c])).join(','))].join('\n');
// Blob-based download (data: URLs are size-limited and blocked in some webviews).
// `doc`/`urlApi` are injectable so the helper is testable without a DOM.
export function downloadText(filename, text, type = 'text/csv;charset=utf-8', doc = typeof document !== 'undefined' ? document : null, urlApi = typeof URL !== 'undefined' ? URL : null) {
  if (!doc || !urlApi || typeof Blob === 'undefined') return false;
  const blob = new Blob([text], { type });
  const href = urlApi.createObjectURL(blob);
  const a = doc.createElement('a');
  a.href = href; a.download = filename; a.style.display = 'none';
  doc.body.appendChild(a); a.click(); doc.body.removeChild(a);
  setTimeout(() => { try { urlApi.revokeObjectURL(href); } catch {} }, 1000);
  return true;
}

// ─── Audit filters ───
// Date inputs give YYYY-MM-DD; the API wants ISO instants. since = start of day, until = end of day (UTC).
export const dayStartIso = (d) => (d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T00:00:00.000Z` : null);
export const dayEndIso = (d) => (d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T23:59:59.999Z` : null);
// Request body for audit_log / audit_export from the filter form (empty fields omitted).
// The action filter travels as `action_filter`: `action` is the request's own action name,
// so sending `action` would clobber it. The API reads `action_filter` server-side and
// narrows the query itself, so there is no client-side re-filtering left to do.
export function auditFilterBody(f) {
  const out = {};
  if (f?.action) out.action_filter = f.action;
  if (f?.actor && f.actor.trim()) out.actor_email = f.actor.trim().toLowerCase();
  if (f?.target && f.target.trim()) out.target = f.target.trim();
  const since = dayStartIso(f?.since), until = dayEndIso(f?.until);
  if (since) out.since = since;
  if (until) out.until = until;
  return out;
}
// Distinct actions in the entries so far, sorted; keeps the currently selected one visible.
export const distinctActions = (entries, selected) => [...new Set([...(entries || []).map(e => e.action).filter(Boolean), ...(selected ? [selected] : [])])].sort();

// ─── Branding ───
// #abc → #aabbcc; #aabbcc(ff) → #aabbcc; anything else → null. <input type=color> only accepts 6-digit.
export function normalizeHex(v) {
  if (typeof v !== 'string') return null;
  const s = v.trim();
  const m3 = /^#([0-9a-f]{3})$/i.exec(s);
  if (m3) return '#' + m3[1].split('').map(c => c + c).join('').toLowerCase();
  const m6 = /^#([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(s);
  if (m6) return '#' + m6[1].toLowerCase();
  return null;
}
export const APP_NAME_MAX = 24;
export const isSvgFile = (file) => !!file && (String(file.type || '').toLowerCase() === 'image/svg+xml' || /\.svg$/i.test(file.name || ''));

// ─── Members ───
export const memberLabel = (m) => (m?.name && m.name !== (m.email || '').split('@')[0] ? `${m.name} (${m.email})` : (m?.email || ''));

// ─── One member count, not four ───
// Four places used to show a different number for "members" because each counted a
// different set (list = clients, detail KPI = clients, tab chip = everyone active,
// Members tab header = the filtered page total). These derive all of them from one
// role breakdown so every label can say exactly which set it means.
const intOf = (v) => { const n = Number(v); return Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : 0; };
/** `{owner, coach, member}` (list_members.counts / sa_list_orgs columns) → labelled totals. */
export function memberCounts(counts) {
  const owners = intOf(counts?.owner), coaches = intOf(counts?.coach), clients = intOf(counts?.member);
  return { owners, coaches, clients, staff: owners + coaches, total: owners + coaches + clients };
}
/** The same breakdown from sa_org_detail's member rows (each carries `role` + `status`). */
export function countsFromMembers(members) {
  const c = { owner: 0, coach: 0, member: 0 };
  for (const m of members || []) if (m?.status === 'active' && c[m.role] !== undefined) c[m.role]++;
  return c;
}
export const plural = (n, one, many) => `${intOf(n)} ${intOf(n) === 1 ? one : (many || `${one}s`)}`;

// ─── Super-admin gate ───
// Only an explicit `false` gates. `null` / `undefined` mean "not yet known" — the check has
// not answered, or the API build predates the field — and must behave exactly as before,
// letting the call through so the API decides; locking on an unknown would hide the tenant
// screens from the one person who owns them on every cold start. A definite `false` (or the
// API's own refusal) gets this screen instead of the developer-facing super_admin_required
// copy, which tells the reader to edit environment variables and redeploy.
export function superAdminGate({ isSuperAdmin, adminEmailConfigured, errorCode } = {}) {
  if (isSuperAdmin !== false && errorCode !== 'super_admin_required') return { blocked: false };
  return {
    blocked: true,
    title: 'Super-admin only',
    detail: 'Organizations (tenants, branding, seats, suspend) are limited to the single super-admin account.',
    cause: adminEmailConfigured === false
      ? 'ADMIN_EMAIL is not set on the API, so no account is the super-admin yet — set it on the API project and redeploy.'
      : 'You are signed in as an admin, but not as the address ADMIN_EMAIL on the API points at.',
  };
}

// ─── Feature flag keys ───
// Render the keys the API sent, not the client-side constant, so a flag added
// server-side is visible without a member-app redeploy. Extra maps (an effective
// flag map, a per-client override map) contribute their keys too; `fallback` is
// used only when the response carried nothing at all.
export function flagKeysFrom(apiKeys, maps = [], fallback = []) {
  const out = [];
  const push = (k) => { if (typeof k === 'string' && k && !out.includes(k)) out.push(k); };
  (Array.isArray(apiKeys) ? apiKeys : []).forEach(push);
  for (const m of maps) Object.keys(m || {}).forEach(push);
  return out.length ? out : [...new Set(fallback || [])];
}
/** Keys the API knows that this build has no label for — rendered in an "Other" group. */
export const unknownFlagKeys = (keys) => (keys || []).filter(k => typeof k === 'string' && !FLAG_GROUP_KEYS.includes(k));

// ─── White-label health / dashboard formatting ───
// Every ratio the API sends may be null (denominator 0). Render an em dash, never NaN
// and never a 0 presented as fact.
export const SEVERITY_RANK = { ok: 0, warn: 1, error: 2 };
export const severityLabel = (sev) => (sev === 'error' ? 'Action needed' : sev === 'warn' ? 'Warning' : 'Healthy');
export const num = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? '—' : Number(v).toLocaleString());
export const pctText = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? '—' : `${Number(v)}%`);
/** Seconds → a compact age ("42s", "7m", "3h", "2d"); null/negative → em dash. */
export function fmtAge(sec) {
  const n = Number(sec);
  if (sec === null || sec === undefined || !Number.isFinite(n) || n < 0) return '—';
  if (n < 90) return `${Math.round(n)}s`;
  if (n < 5400) return `${Math.round(n / 60)}m`;
  if (n < 172800) return `${Math.round(n / 3600)}h`;
  return `${Math.round(n / 86400)}d`;
}
/** health.whitelabel.cron / dashboard.whitelabel.cron → the tile's severity, label and cause. */
export function cronStatus(cron) {
  if (!cron || cron.ever_run === false) {
    return { severity: 'error', label: 'never run',
      note: 'CRON_SECRET must be set on the API project (Vercel → ironlog-api → Environment Variables) and the API redeployed; until then announcements, seat snapshots and queued email never run.' };
  }
  if (cron.stale) return { severity: 'error', label: `stale · ${fmtAge(cron.age_seconds)} ago`, note: 'The cron runs every 5 minutes and is considered stale after 15.' };
  if (cron.ok === false) return { severity: 'warn', label: `errors · ${fmtAge(cron.age_seconds)} ago`, note: 'The last run finished but reported errors.' };
  return { severity: 'ok', label: `ran ${fmtAge(cron.age_seconds)} ago`, note: 'Running on schedule.' };
}

// ─── Push audience ───
// /api/push cannot target an org, so the recipient list is narrowed here from the
// membership the org API returns. `orgEmails` is every address known to be in an org.
export function filterAudience(users, mode, orgEmails) {
  const set = new Set((orgEmails || []).map(e => String(e || '').toLowerCase()));
  const list = users || [];
  if (mode === 'org') return list.filter(u => set.has(String(u?.email || '').toLowerCase()));
  if (mode === 'solo') return list.filter(u => !set.has(String(u?.email || '').toLowerCase()));
  return list;
}

// ─── user_detail.org ───
export const ADHERENCE_BUCKETS = { on_track: 'On track', slipping: 'Slipping', at_risk: 'At risk', inactive: 'Inactive' };
export const adherenceLabel = (b) => ADHERENCE_BUCKETS[b] || (b ? String(b).replace(/_/g, ' ') : '—');
/** The five consent scopes as [key, label, shared] rows, in the API's order. */
export const CONSENT_SCOPES = [
  ['workouts', 'Workouts'], ['checkins', 'Check-ins'], ['nutrition', 'Nutrition'], ['body', 'Body stats'], ['photos', 'Progress photos'],
];
export const consentRows = (consent) => CONSENT_SCOPES.map(([key, label]) => ({ key, label, shared: consent?.[key] === true }));
